import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../utils/AuthContext';
import { db } from '../../utils/firebase';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { NotificationPrefs, DEFAULT_NOTIFICATION_PREFS } from '../../types';
import { X, Bell, Save, Loader2, CheckCircle, Terminal } from 'lucide-react';
import NotificationSettingsForm from './NotificationSettingsForm';
import type { NotificationAudience } from '../../utils/notificationSettingsMeta';
import { NOTIFICATION_DEBUG_PATH } from '../../utils/notificationDebugCatalog';
import '../pm/pm-shared.css';
import './NotificationSettingsModal.css';

interface Props {
  onClose: () => void;
  /** Defaults from user role when omitted */
  variant?: NotificationAudience;
}

function resolveVariant(role: string | undefined): NotificationAudience {
  if (role === 'architect' || role === 'designer' || role === 'engineer') return 'portal';
  return 'internal';
}

const NotificationSettingsModal: React.FC<Props> = ({ onClose, variant: variantProp }) => {
  const { user } = useAuth();
  const variant = variantProp ?? resolveVariant(user?.role);
  const [prefs, setPrefs] = useState<NotificationPrefs>({ ...DEFAULT_NOTIFICATION_PREFS });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.id));
        if (cancelled || !userDoc.exists()) return;
        const data = userDoc.data();
        if (data.notificationPrefs) {
          setPrefs({ ...DEFAULT_NOTIFICATION_PREFS, ...data.notificationPrefs });
        }
      } catch (err) {
        console.error('[NotificationSettingsModal] Load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleSave = async () => {
    if (!user?.id) return;
    setSaving(true);
    setSaveStatus('idle');
    try {
      const userRef = doc(db, 'users', user.id);
      const userDoc = await getDoc(userRef);
      const prev = (userDoc.exists() ? userDoc.data().notificationPrefs : null) as Partial<NotificationPrefs> | undefined;
      const next: NotificationPrefs = {
        ...DEFAULT_NOTIFICATION_PREFS,
        ...(prev || {}),
        ...prefs,
      };
      if (userDoc.exists()) {
        await updateDoc(userRef, { notificationPrefs: next });
      } else {
        await setDoc(userRef, { notificationPrefs: next }, { merge: true });
      }
      setSaveStatus('saved');
      setTimeout(() => onClose(), 900);
    } catch (err) {
      console.error('[NotificationSettingsModal] Save error:', err);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const handlePrefsChange = useCallback((next: NotificationPrefs) => {
    setPrefs(next);
    setSaveStatus('idle');
  }, []);

  const openNotificationDebug = () => {
    window.open(NOTIFICATION_DEBUG_PATH, '_blank', 'noopener,noreferrer');
  };

  const isAdmin = user?.role === 'admin';

  return (
    <div
      className="notif-modal-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="notif-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="notif-modal-title"
      >
        <header className="notif-modal-header">
          <div className="notif-modal-header-text">
            <div className="notif-modal-icon" aria-hidden>
              <Bell size={18} />
            </div>
            <div>
              <h2 id="notif-modal-title">Notification Settings</h2>
              <p>Personal preferences — bell and email. #CRM only posts milestones.</p>
            </div>
          </div>
          <button
            type="button"
            className="notif-modal-close"
            onClick={onClose}
            aria-label="Close notification settings"
          >
            <X size={18} />
          </button>
        </header>

        <div className="notif-modal-body">
          {loading ? (
            <div className="notif-modal-loading">
              <Loader2 size={28} className="notif-modal-spin" aria-hidden />
              <span>Loading preferences…</span>
            </div>
          ) : (
            <NotificationSettingsForm
              prefs={prefs}
              onChange={handlePrefsChange}
              variant={variant}
              userEmail={user?.email}
            />
          )}
        </div>

        <footer className="notif-modal-footer">
          {saveStatus === 'error' && (
            <p className="notif-modal-error" role="alert">
              Could not save preferences. Please try again.
            </p>
          )}
          <div className="notif-modal-footer-actions">
            {isAdmin && (
              <button
                type="button"
                className="notif-modal-btn notif-modal-btn--debug"
                onClick={openNotificationDebug}
              >
                <Terminal size={14} aria-hidden />
                Notification debug
              </button>
            )}
            <div className="notif-modal-footer-actions-main">
            <button type="button" className="notif-modal-btn notif-modal-btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="notif-modal-btn notif-modal-btn--primary"
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving ? (
                <>
                  <Loader2 size={14} className="notif-modal-spin" aria-hidden />
                  Saving…
                </>
              ) : saveStatus === 'saved' ? (
                <>
                  <CheckCircle size={14} aria-hidden />
                  Saved
                </>
              ) : (
                <>
                  <Save size={14} aria-hidden />
                  Save preferences
                </>
              )}
            </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default NotificationSettingsModal;
